import { useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useSocialShareModal } from 'containers/SocialShareModal/SocialShareModalContext';
import useOnShare from 'containers/SocialShareModal/useOnShare';
import useTwitterAuthenticator from 'hooks/useTwitterAuthenticator';
import {
  scheduleTwitterVideo,
  shareTwitterVideo,
} from 'redux/modules/download';
import { twitterSelector } from 'redux/modules/social';
import { Dispatch } from 'redux/types';
import { SocialSharePlatformData } from '../types';
import { useTwitterPostComposerStep } from './steps';
import { TwitterSocialShareStep } from './types';

export default function useTwitterSocialShare(): SocialSharePlatformData<
  TwitterSocialShareStep
> {
  const { providerUserId, profileImageUrl, username, postStatus } = useSelector(
    twitterSelector,
  );

  const dispatch = useDispatch<Dispatch>();

  const { description, onAuthSuccess, onAuthError } = useSocialShareModal();

  const { authenticating, authenticate } = useTwitterAuthenticator({
    force: true,
    onAuthSuccess,
    onError: onAuthError,
  });

  const handleShare = useOnShare({
    shareNow: useCallback(() => dispatch(shareTwitterVideo(description)), [
      description,
      dispatch,
    ]),
    schedule: useCallback(
      (scheduledDate: number) =>
        dispatch(scheduleTwitterVideo(description, scheduledDate)),
      [description, dispatch],
    ),
  });

  const twitterPostComposerStep = useTwitterPostComposerStep({
    onShare: handleShare,
  });

  return {
    platformAlias: 'x',
    authenticating: authenticating || !username,
    accounts: [
      {
        id: providerUserId,
        avatarSrc: profileImageUrl,
        username,
        handle: username,
      },
    ],
    steps: [twitterPostComposerStep],
    defaultStep: twitterPostComposerStep.id,
    postStatus,
    authenticate,
  };
}
